import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Home, Edit3, Settings, Video, ChevronLeft, ChevronRight, Server } from 'lucide-react';

const SidebarContainer = styled.div`
  width: ${props => props.collapsed ? '60px' : '240px'};
  background-color: ${props => props.theme.surface};
  border-right: 1px solid ${props => props.theme.border};
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  transition: width 0.3s ease;
  position: relative;
`;

const CollapseButton = styled.button`
  position: absolute;
  top: 20px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  color: ${props => props.theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.text};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 30px;
  ${props => props.collapsed && `
    justify-content: center;
    padding: 0 8px;
  `}
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.collapsed ? '0' : '12px'};
  transition: margin 0.3s ease;
`;

const LogoText = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0;
  opacity: ${props => props.collapsed ? '0' : '1'};
  transform: ${props => props.collapsed ? 'translateX(-10px)' : 'translateX(0)'};
  transition: all 0.3s ease;
  ${props => props.collapsed && 'position: absolute; pointer-events: none;'}
`;

const Navigation = styled.nav`
  flex: 1;
  padding: 0 12px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? props.theme.primary : props.theme.textSecondary};
  background-color: ${props => props.active ? `${props.theme.primary}15` : 'transparent'};
  position: relative;
  ${props => props.collapsed && `
    justify-content: center;
    padding: 12px 8px;
  `}

  &:hover {
    background-color: ${props => props.active ? `${props.theme.primary}15` : `${props.theme.border}50`};
    color: ${props => props.active ? props.theme.primary : props.theme.text};
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: ${props => props.collapsed ? '0' : '12px'};
    transition: margin 0.3s ease;
  }
`;

const NavText = styled.span`
  font-size: 14px;
  font-weight: 500;
  opacity: ${props => props.collapsed ? '0' : '1'};
  transform: ${props => props.collapsed ? 'translateX(-10px)' : 'translateX(0)'};
  transition: all 0.3s ease;
  ${props => props.collapsed && 'position: absolute; pointer-events: none;'}
`;

const Tooltip = styled.div`
  position: absolute;
  left: 60px;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  box-shadow: ${props => props.theme.shadow};
  border: 1px solid ${props => props.theme.border};
  z-index: 1000;

  ${NavItem}:hover & {
    opacity: ${props => props.show ? '1' : '0'};
  }
`;

const Footer = styled.div`
  padding: 0 20px;
  margin-top: auto;
  ${props => props.collapsed && `
    padding: 0 8px;
    text-align: center;
  `}
`;

const Version = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  text-align: center;
  opacity: ${props => props.collapsed ? '0' : '1'};
  transition: opacity 0.3s ease;
`;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    {
      path: '/home',
      icon: Home,
      label: 'Projects',
      description: 'Manage your video projects'
    },
    {
      path: '/edit',
      icon: Edit3,
      label: 'Editor',
      description: 'Edit and process videos'
    },
    {
      path: '/settings',
      icon: Settings,
      label: 'Settings',
      description: 'Configure API keys and preferences'
    },
    {
      path: '/agent-dashboard',
      icon: Server,
      label: 'Local Agent',
      description: 'Monitor local processing agent'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <SidebarContainer collapsed={collapsed}>
      <CollapseButton onClick={toggleCollapse}>
        {collapsed ? <ChevronRight /> : <ChevronLeft />}
      </CollapseButton>

      <Logo collapsed={collapsed}>
        <LogoIcon collapsed={collapsed}>
          <Video size={18} color="white" />
        </LogoIcon>
        <LogoText collapsed={collapsed}>BeeVids</LogoText>
      </Logo>

      <Navigation>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
                          (item.path === '/edit' && location.pathname.startsWith('/edit'));
          
          return (
            <NavItem
              key={item.path}
              active={isActive}
              collapsed={collapsed}
              onClick={() => handleNavigation(item.path)}
              title={collapsed ? item.description : ''}
            >
              <Icon />
              <NavText collapsed={collapsed}>{item.label}</NavText>
              {collapsed && (
                <Tooltip show={collapsed}>
                  {item.label}
                </Tooltip>
              )}
            </NavItem>
          );
        })}
      </Navigation>

      <Footer collapsed={collapsed}>
        <Version collapsed={collapsed}>BeeVids v1.0.0</Version>
      </Footer>
    </SidebarContainer>
  );
};

export default Sidebar;